import { useState } from 'react';
import type { ReactElement } from 'react';
import { assetToast, formatDate } from '@xdocs/i18n';
import type { AdminAssetRow, ModerationStatus } from '@xdocs/schemas';
import { Badge, DataTable, SegmentedControl, cn, useI18n, useToast } from '@xdocs/ui';
import type { BadgeTone, ColumnDef } from '@xdocs/ui';
import { RoleGate } from '../../../features/admin/index.js';
import { optionalProp } from '../../../lib/optionalProp.js';
import { AssetActionDialog } from './AssetActionDialog.js';
import { AssetDetailPanel } from './AssetDetailPanel.js';
import { AssetPreviewThumb } from './AssetPreviewThumb.js';
import { useAssetsList } from './useAssetsList.js';
import type { AssetFilters } from './useAssetsList.js';
import { useAssetsT } from './useAssetsT.js';
import styles from './AssetsPage.module.css';

const MODERATION_TONE: Record<ModerationStatus, BadgeTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

interface PendingAction {
  asset: AdminAssetRow;
  mode: 'unpublish' | 'remove';
}

/**
 * `/admin/assets` (F60) — control-all-generated-assets on the shared
 * `<DataTable>` (E4 §8.3). Moderation-verdict override isn't wired: no
 * `admin.moderation.*` override op exists in this task's frozen WS
 * contracts (only `admin.assets.list/unpublish/remove`) — see returned
 * open-issues list.
 *
 * Wrapped in `<RoleGate minimum="admin">` (`features/admin/`, sibling M4
 * task) — F60 is `admin`-only, distinct from the moderator `F69` takedown
 * surface; see `PromosPage.tsx`'s doc comment for the defense-in-depth
 * rationale (UX-only, server enforces on every op).
 */
export function AssetsPage(): ReactElement {
  const { locale } = useI18n();
  const { t } = useAssetsT();
  const toast = useToast();
  const [filters, setFilters] = useState<AssetFilters>({});
  const { rows, total, nextCursor, query, onQueryChange, onLoadMore, isLoading, isLoadingMore, isError, loadMoreError, onRetry, invalidate } =
    useAssetsList(filters);
  const [inspecting, setInspecting] = useState<AdminAssetRow | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  function handleDone(assetId: string): void {
    const asset = rows.find((row) => row.id === assetId);
    if (pendingAction && asset) {
      toast.push(
        assetToast(
          locale,
          'success',
          t(pendingAction.mode === 'unpublish' ? 'adminAssets:toast.unpublished.action' : 'adminAssets:toast.removed.action'),
          asset.title,
        ),
      );
    }
    setPendingAction(null);
    setInspecting(null);
    invalidate();
  }

  const columns: ColumnDef<AdminAssetRow>[] = [
    {
      id: 'title',
      header: t('adminAssets:list.column.title'),
      sortable: true,
      primary: true,
      cell: (row) => (
        <div className={cn(styles['titleCell'])}>
          <AssetPreviewThumb preview={row.preview} alt="" />
          <span>{row.title}</span>
        </div>
      ),
    },
    {
      id: 'type',
      header: t('adminAssets:list.column.type'),
      // mockup-45: design/app/45-admin-assets.html renders Type as plain
      // lowercase mono text, not a badge/chip.
      cell: (row) => <span className={cn(styles['metaCell'], styles['lowercase'])}>{t(`adminAssets:type.${row.type}`)}</span>,
    },
    {
      id: 'owner',
      header: t('adminAssets:list.column.owner'),
      hideOnMobile: true,
      cell: (row) => <span>{row.ownerDisplayName ?? row.ownerId}</span>,
    },
    {
      id: 'moderationStatus',
      header: t('adminAssets:list.column.moderation'),
      cell: (row) =>
        row.moderationStatus ? (
          <Badge tone={MODERATION_TONE[row.moderationStatus]}>{t(`adminAssets:moderation.${row.moderationStatus}`)}</Badge>
        ) : (
          <span className={cn(styles['metaCell'])}>{t('adminAssets:moderation.none')}</span>
        ),
    },
    {
      id: 'createdAt',
      header: t('adminAssets:list.column.createdAt'),
      sortable: true,
      hideOnMobile: true,
      cell: (row) => <span className={cn(styles['metaCell'])}>{formatDate(locale, row.createdAt, { dateStyle: 'medium' })}</span>,
    },
    {
      id: 'actions',
      header: t('adminAssets:list.column.actions'),
      // mockup-45: design/app/45-admin-assets.html's Actions column
      // (Inspect/Unpublish/Remove inline text links) — hidden on mobile
      // since the shared `<DataTable>` mobile card already wraps every cell
      // in one outer `<button onRowClick>`; nesting these buttons inside it
      // would be invalid HTML. Mobile users reach the same actions via the
      // card tap -> `AssetDetailPanel`'s Unpublish/Remove buttons.
      hideOnMobile: true,
      cell: (row) => (
        // No handlers on this wrapper `<div>` (jsx-a11y/no-static-element-
        // interactions) — each button below stops its own propagation so a
        // click/Enter/Space doesn't also bubble to the row's onRowClick.
        <div className={cn(styles['actionsCell'])}>
          <button
            type="button"
            className={cn(styles['actionLink'], styles['actionNeutral'])}
            onClick={(event) => {
              event.stopPropagation();
              setInspecting(row);
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {t('adminAssets:action.inspect')}
          </button>
          {row.type !== 'file' && (
            <button
              type="button"
              className={cn(styles['actionLink'], styles['actionWarning'])}
              onClick={(event) => {
                event.stopPropagation();
                setPendingAction({ asset: row, mode: 'unpublish' });
              }}
              onKeyDown={(event) => event.stopPropagation()}
            >
              {t('adminAssets:action.unpublish')}
            </button>
          )}
          <button
            type="button"
            className={cn(styles['actionLink'], styles['actionDanger'])}
            onClick={(event) => {
              event.stopPropagation();
              setPendingAction({ asset: row, mode: 'remove' });
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {t('adminAssets:action.remove')}
          </button>
        </div>
      ),
    },
  ];

  return (
    <RoleGate minimum="admin">
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('adminAssets:page.title')}</h1>
        <p className={cn(styles['description'])}>{t('adminAssets:page.description')}</p>
      </div>

      {/* mockup-45: design/app/45-admin-assets.html replaces the toolbar
          `<select>` dropdowns with a row of rounded pill filter buttons — the
          shared `SegmentedControl` primitive (`@xdocs/ui`) is the same
          rounded-pill/single-select-group family. The mock's row is a single
          "status" dimension (all/approved/flagged/pending/rejected); this
          schema has no `flagged` moderation status and separately tracks
          asset `type`, so this keeps two groups (Type, Moderation) rather
          than dropping the Type filter mock didn't need for its 5-row demo
          data — see returned open-issues list. */}
      <div className={cn(styles['filterBar'])}>
        <div className={cn(styles['filterGroup'])}>
          <span className={cn(styles['filterGroupLabel'])}>{t('adminAssets:filter.type.label')}</span>
          <SegmentedControl
            label={t('adminAssets:filter.type.label')}
            value={filters.type ?? ''}
            onChange={(value) => setFilters((prev) => ({ ...prev, type: value || undefined }))}
            options={[
              { value: '', label: t('adminAssets:filter.type.all') },
              { value: 'document', label: t('adminAssets:type.document') },
              { value: 'share', label: t('adminAssets:type.share') },
              { value: 'file', label: t('adminAssets:type.file') },
            ]}
          />
        </div>
        <div className={cn(styles['filterGroup'])}>
          <span className={cn(styles['filterGroupLabel'])}>{t('adminAssets:filter.moderation.label')}</span>
          <SegmentedControl
            label={t('adminAssets:filter.moderation.label')}
            value={filters.moderationStatus ?? ''}
            onChange={(value) => setFilters((prev) => ({ ...prev, moderationStatus: value || undefined }))}
            options={[
              { value: '', label: t('adminAssets:filter.moderation.all') },
              { value: 'pending', label: t('adminAssets:moderation.pending') },
              { value: 'approved', label: t('adminAssets:moderation.approved') },
              { value: 'rejected', label: t('adminAssets:moderation.rejected') },
            ]}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => `${row.type}:${row.id}`}
        query={query}
        onQueryChange={onQueryChange}
        label={t('adminAssets:page.title')}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        total={total}
        nextCursor={nextCursor}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        {...optionalProp('loadMoreError', loadMoreError)}
        emptyTitle={t('adminAssets:list.empty.title')}
        emptyDescription={t('adminAssets:list.empty.description')}
        noResultsTitle={t('adminAssets:list.noResults.title', { query: query.q ?? '' })}
        onClearFilters={() => {
          const { q: _q, ...rest } = query;
          onQueryChange({ ...rest, page: 1 });
        }}
        searchLabel={t('adminAssets:list.searchLabel')}
        searchPlaceholder={t('adminAssets:list.searchPlaceholder')}
        onRowClick={(row) => setInspecting(row)}
      />

      {inspecting && !pendingAction && (
        <AssetDetailPanel
          asset={inspecting}
          onClose={() => setInspecting(null)}
          onUnpublish={() => setPendingAction({ asset: inspecting, mode: 'unpublish' })}
          onRemove={() => setPendingAction({ asset: inspecting, mode: 'remove' })}
        />
      )}

      {pendingAction && (
        <AssetActionDialog
          asset={pendingAction.asset}
          mode={pendingAction.mode}
          onClose={() => setPendingAction(null)}
          onDone={handleDone}
        />
      )}
    </div>
    </RoleGate>
  );
}
